import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type VendorResource,
  VendorsController_create_v1,
  type VendorsController_create_v1Body,
  VendorsController_retire_v1,
  VendorsController_update_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { optionalGstinSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';

/**
 * One vendor: create (`POST /v1/vendors`), edit (`PATCH /v1/vendors/{id}`) and
 * retire (`DELETE /v1/vendors/{id}`), in one dialog the way `EditOutletDialog`
 * holds an outlet.
 *
 * **Retiring cannot be undone** except by creating a new vendor, and the
 * confirmation says so. The retired vendor stays on every expense that names it
 * and its name becomes free — so a retired vendor still opens, read-only, because
 * the API answers an edit or retire of one with `RESOURCE_NOT_FOUND`.
 *
 * The bounds below are the API's own (`CreateVendorDto`, and the GSTIN shape in
 * its `vendors.service.ts`, shared through `lib/field-schemas.ts` with the
 * park's own). They are a courtesy — the API still decides.
 */

const vendorSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Enter what the supplier is called.')
    .max(100, 'A name is at most 100 characters.'),
  phone: z.string().trim().max(20, 'A phone number is at most 20 characters.'),
  gstin: optionalGstinSchema,
  notes: z.string().trim().max(500, 'Notes are at most 500 characters.'),
});

type VendorValues = z.infer<typeof vendorSchema>;

/** Blank is "none": `null` on the wire, which also clears the field on an edit. */
const orNull = (value: string): string | null => (value === '' ? null : value);

const bodyOf = (values: VendorValues): VendorsController_create_v1Body => ({
  name: values.name,
  phone: orNull(values.phone),
  gstin: orNull(values.gstin),
  notes: orNull(values.notes),
});

export function VendorDialog({
  vendor,
  onOpenChange,
  onSaved,
  onRetired,
}: {
  /** `null` to create one. */
  vendor: VendorResource | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (vendor: VendorResource, created: boolean) => void;
  onRetired: (vendor: VendorResource) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <FormDialogContent>
        {vendor !== null && vendor.retiredAt !== null ? (
          <RetiredVendor vendor={vendor} />
        ) : (
          <EditableVendor vendor={vendor} onSaved={onSaved} onRetired={onRetired} />
        )}
      </FormDialogContent>
    </Dialog>
  );
}

function EditableVendor({
  vendor,
  onSaved,
  onRetired,
}: {
  vendor: VendorResource | null;
  onSaved: (vendor: VendorResource, created: boolean) => void;
  onRetired: (vendor: VendorResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(vendorSchema),
    mode: 'onTouched',
    defaultValues: {
      name: vendor?.name ?? '',
      phone: vendor?.phone ?? '',
      gstin: vendor?.gstin ?? '',
      notes: vendor?.notes ?? '',
    },
  });

  const [confirmingRetire, setConfirmingRetire] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Per body for the editable write, per mount for the retire — see
  // `lib/write-attempt.ts` and `EditOutletDialog`.
  const attempt = useRef<WriteAttempt | null>(null);
  const [retireKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting || retiring;

  const handleSubmit = async (values: VendorValues) => {
    if (pending) return;

    setFormError(null);

    const body = bodyOf(values);
    const sending = attemptFor(attempt.current, JSON.stringify(body));
    attempt.current = sending;
    const headers = { [IDEMPOTENCY_KEY_HEADER]: sending.key };

    try {
      if (vendor === null) {
        onSaved(await client.request(VendorsController_create_v1, { body, headers }), true);
      } else {
        onSaved(
          await client.request(VendorsController_update_v1, {
            path: { id: vendor.id },
            body,
            headers,
          }),
          false,
        );
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EXPENSE_VENDOR_NAME_TAKEN') {
        form.setError('name', { message: messageForFailure(error) });
        form.setFocus('name');
      } else {
        setFormError(messageForFailure(error));
      }
    }
  };

  const handleRetire = async () => {
    if (pending || vendor === null) return;

    setFormError(null);
    setRetiring(true);

    try {
      onRetired(
        await client.request(VendorsController_retire_v1, {
          path: { id: vendor.id },
          headers: { [IDEMPOTENCY_KEY_HEADER]: retireKey },
        }),
      );
    } catch (error) {
      setFormError(messageForFailure(error));
      setConfirmingRetire(false);
    } finally {
      setRetiring(false);
    }
  };

  if (vendor !== null && confirmingRetire) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Retire {vendor.name}?</DialogTitle>
          <DialogDescription>This cannot be undone.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm text-foreground">
          <p>
            Expenses already paid to this vendor keep naming it. No new expense can name it, and its
            name becomes free for a new vendor.
          </p>
          <p>
            There is no way to bring a retired vendor back. If you need it again, create a new
            vendor.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={() => setConfirmingRetire(false)}>
            Keep it
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void handleRetire()}>
            {pending ? 'Retiring…' : 'Yes, retire it'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{vendor === null ? 'New vendor' : vendor.name}</DialogTitle>
        <DialogDescription>
          A supplier the park pays. Vendors are park-wide, not per outlet.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Name
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Sri Balaji Traders"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>Phone</FormLabel>
                <FormControl
                  render={
                    <Input
                      type="tel"
                      className={`${fieldSurface} h-11 font-mono numeric`}
                      placeholder="+91 98450 12345"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gstin"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>GSTIN</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 font-mono tracking-wider uppercase numeric`}
                      placeholder="29ABCDE1234F1Z5"
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={15}
                      {...field}
                    />
                  }
                />
                <FormDescription>15 characters. Stored in capitals.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>Notes</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      placeholder="Supplies cafe vegetables, paid weekly"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className={vendor === null ? undefined : 'sm:justify-between'}>
            {vendor !== null && (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                disabled={pending}
                onClick={() => setConfirmingRetire(true)}
              >
                Retire vendor
              </Button>
            )}

            {/* On an edit, disabled until something differs: a PATCH that
                changes nothing is refused, and would be an audit row besides. */}
            <Button
              type="submit"
              disabled={pending || (vendor !== null && !form.formState.isDirty)}
            >
              {pending ? 'Saving…' : vendor === null ? 'Create vendor' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

/** A retired vendor, open and read-only: every edit or retire would be refused. */
function RetiredVendor({ vendor }: { vendor: VendorResource }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-3">
          {vendor.name}
          <Badge variant="outline">Retired</Badge>
        </DialogTitle>
        <DialogDescription>
          Kept because expenses name it. Nothing here can be changed; to pay this supplier again,
          create a new vendor.
        </DialogDescription>
      </DialogHeader>

      <dl className="space-y-4 text-sm">
        {(
          [
            ['Phone', vendor.phone],
            ['GSTIN', vendor.gstin],
            ['Notes', vendor.notes],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="space-y-1.5">
            <dt className={fieldLabel}>{label}</dt>
            <dd className={value === null ? 'text-muted-foreground' : 'text-foreground'}>
              {value ?? 'None'}
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}
